import React, { useCallback, useEffect, useMemo, useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Theme,
  useMediaQuery
} from '@mui/material';
import { FormikErrors, FormikValues, useFormik } from 'formik';
import { TwButton, TwButtonType } from '../../components/TwButtons/TwButtons';
import TwCheckbox from '../../components/TwCheckboxes/TwCheckboxes';
import TwDialog from '../../components/TwDialog/TwDialog';
import TwField, { TwInputTypes } from '../../components/TwFields/TwFields';
import TwTooltip from '../../components/TwTooltip/TwTooltip';
import useAuth from '../../providers/Auth/Auth.provider';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import { LoadDocumentType, ShareType } from '../../redux/api/load/load.model';
import LoadsService from '../../providers/Loads/Loads.service';
import {
  ClientResponse,
  CreateClientData,
  Preferences
} from '../../redux/api/customer/customer.model';
import StringUtils from '../../utils/stringUtils';
import { ReactComponent as Warning } from '../../assets/icons/warning.svg';
import TwDocumentViewer, {
  TwDocumentType
} from '../../components/TwDocumentViewer/TwDocumentViewer';
import './EditInvoice.scss';
import NumberUtils from '../../utils/numberUtils';
import {
  InvoiceItemV3,
  InvoicePreviewPdfData,
  InvoiceResponseV3,
  InvoiceSources,
  InvoiceState,
  InvoiceUpdateData,
  InvoiceUpdateDataV3
} from '../../providers/Invoices/Invoices.model';
import SettingsService from '../../providers/Settings/Settings.service';
import { AccountData } from '../../providers/Settings/Account.model';
import BillFrom, { BillFromData } from './../CreateInvoice/BillFrom/BillFrom';
import BillTo, { BillToData } from './BillTo/BillTo';
import TwDatePicker from '../../components/TwDatePicker/TwDatePicker';
import InvoiceItems, { RateItem } from './InvoiceItems/InvoiceItems';
import TwAutocomplete, {
  TwAutocompleteOption
} from '../../components/TwAutocomplete/TwAutocomplete';
import TwTagsField, { TwTagsOption } from '../../components/TwTagsField/TwTagsField';
import UploadBol from '../UploadBol/UploadBol';
import TwSelect, { TwSelectOption } from '../../components/TwSelects/TwSelects';
import InvoicesService from '../../providers/Invoices/Invoices.service';
import { useUpdateCustomerMutation } from '../../redux/api/customer/customerApi';
import PreviewInvoice, {
  PreviewInvoiceSendData
} from '../CreateInvoice/PreviewInvoice/PreviewInvoice';
import { getCustomerContactEmails } from '../../utils/contactUtils';
import TwShareHistory from '../../components/TwLoadShareHistory/TwLoadShareHistory';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  closeUploadBol,
  setLoadDetailReload
} from '../../redux/features/navigation/navigationSlice';
import {
  setInvoiceDivisionId,
  setInvoiceLoadIds,
  setOrderId
} from '../../redux/features/orders/ordersSlice';
import { RowData } from '../../components/TwAdvancedRates/RateRow';
import { nanoid } from '@reduxjs/toolkit';
import {
  InvoiceService,
  InvoiceServiceStatus,
  InvoiceServiceUnit
} from '../../redux/api/invoiceService/invoiceService.model';
import { LoadsRates } from '../CreateInvoice/LoadRates/LoadsRates';
import useInvoiceDefaultDescription from '../../hooks/useInvoiceDefaultDescripion';
import TwSelectableDocsPhotos from '../../components/TwSelectableDocsPhotos/TwSelectableDocsPhotos.';
import { ReactComponent as QBOIcon } from '../../assets/icons/QBO-icon.svg';
import {
  getCarriersAndCompanyIds,
  getErrorInfo,
  scrollToInput
} from '../CreateInvoice/CreateInvoice';
import { CreateLocationData } from '../../redux/api/location/location.model';
import FileUtils from '../../utils/fileUtils';
import { useGetInvoiceServicesQuery } from '../../redux/api/invoiceService/invoiceServiceApi';
import { logRocketTrack } from '../../utils/logRocketUtils';
import useLocalStorageState from 'use-local-storage-state';
import useFeatureFlags from '../../providers/FeatureFlags/FeatureFlags.provider';
import DateUtils from '../../utils/dateUtils';
import { CarrierResponse } from '../../redux/api/carriers/carriers.model';
import { useGetTenantAccountingSettingsQuery } from '../../redux/api/tenant/tenantApi';
import TwQBOLoader from '../../components/TwQBOLoader/TwQBOLoader';
import TwAlert from '../../components/TwAlerts/TwAlerts';
import { ReactComponent as AlertIcon } from '../../assets/alert.svg';
import TwInfoModal from '../../components/TwInfoModal/TwInfoModal';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import BillFromQBO from '../CreateInvoice/BillFromQBO/BillFromQBO';
import { Order, OrderAttachment } from '../../redux/api/orders/orders.model';
import TwOrderTripsDocViewer from '../../components/TwOrderTripsDocViewer/TwOrderTripsDocViewer';
import TwUploadOrderTripFiles from '../../components/TwUploadOrderTripFiles/TwUploadOrderTripFiles';
import FactorInvoice from '../CreateInvoice/FactorInvoice/FactorInvoice';
import FactoringService from '../../providers/Factoring/Factoring.service';
import FactoringUtils from '../../utils/factoringUtils';
import {
  useGetQBOClassesByDivisionQuery,
  useGetQBOClassTrackingQuery
} from '../../redux/api/divisions/divisionsApi';
import { useGetOrdersForInvoiceMutation } from '../../redux/api/orders/ordersApi';
import { useGetInternalFactoringCompanyQuery } from '../../redux/api/internalFactoring/internalFactoringApi';
import InvoiceUtils from '../../utils/invoiceUtils';
import SalesTaxService, { SalesTax } from '../../providers/Settings/SalesTax.service';
import SalesTaxCreateUpdateModal from '../Settings/Invoicing/SalesTax/CreateUpdateModal';

export interface EditInvoiceProps {
  open: boolean;
  invoiceId: string;
  divisionId: string;
  onClickClose: () => void;
  setReloadInvoices?: React.Dispatch<React.SetStateAction<boolean>>;
  setEditInvoiceOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setViewInvoiceBlob: React.Dispatch<React.SetStateAction<Blob | undefined>>;
  setViewInvoiceOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onDeleteInvoiceFromPreview?: () => void;
}

interface InvoiceRateItem {
  description: string;
  amount: number;
  product?: string;
}

export interface EditInvoiceFormValues {
  invoiceNumber: string;
  source: InvoiceSources;
  invoiceItems: RateItem[];
  documents: OrderAttachment[];
  sendInvoice: boolean;
  tenantCcInvoice: boolean;
  companyEmail: string;
  photos: OrderAttachment[];
  paymentTerm: string;
  customDueDate: Date | null;
  specialInstructions: string;
  billFromCompanyId?: string | null;
  qboClassId?: string;
  salesTaxId?: string;
  salesTaxRate?: number;
}

const PAYMENT_TERMS: TwSelectOption[] = [
  {
    label: 'Within 30 days',
    value: '30'
  },
  {
    label: 'Upon receipt',
    value: '0'
  },
  {
    label: 'Within 15 days',
    value: '15'
  },

  {
    label: 'Within 45 days',
    value: '45'
  },
  {
    label: 'Within 60 days',
    value: '60'
  },
  {
    label: 'Other',
    value: 'Other'
  }
];

const INITIAL_INVOICE_DATA = {
  source: InvoiceSources.TB,
  invoiceNumber: '',
  invoiceItems: [],
  documents: [],
  sendInvoice: false,
  tenantCcInvoice: true,
  companyEmail: '',
  photos: [],
  paymentTerm: PAYMENT_TERMS[0].value,
  customDueDate: null,
  specialInstructions: '',
  qboClassId: '',
  salesTaxId: '',
  salesTaxRate: 0
};

const EditInvoice: React.FC<EditInvoiceProps> = ({
  open,
  setEditInvoiceOpen,
  setViewInvoiceBlob,
  invoiceId,
  divisionId,
  onClickClose,
  setReloadInvoices,
  setViewInvoiceOpen,
  onDeleteInvoiceFromPreview
}): JSX.Element => {
  // #region hooks
  const dispatch = useAppDispatch();
  const { addNotification, setIsShowing } = useNotifications();
  const { enableSendQboInvoiceInternally, enableZipAttachments, enableFactoring } =
    useFeatureFlags();
  const {
    data: { userData },
    getTenantSetting
  } = useAuth();
  const commoditiesEnabled =
    (userData?.tenantSettings && userData.tenantSettings['load-commodities']) || false;
  const isUploadBolOpen = useAppSelector((state) => state.navigation.isUploadBolOpen);
  const [invoiceDefaultDescriptionMaker] = useInvoiceDefaultDescription();
  const [mergeAttachments, setMergeAttachments] = useLocalStorageState('mergeAttachments', {
    defaultValue: false
  });
  const [zipAttachments, setZipAttachments] = useLocalStorageState('zipAttachments', {
    defaultValue: false
  });

  // #region redux
  const {
    data: accountingSettings,
    isFetching: areAccountingSettingsFetching,
    isLoading: areAccountingSettingsLoading
  } = useGetTenantAccountingSettingsQuery(
    {
      divisionId: divisionId
    },
    { skip: !divisionId }
  );
  const {
    data: invoiceServicesData,
    isFetching: areInvoiceServicesFetching,
    isLoading: areInvoiceServicesLoading
  } = useGetInvoiceServicesQuery(
    {
      filters: {
        status: [InvoiceServiceStatus.ACTIVE, InvoiceServiceStatus.DISABLED],
        divisionId: divisionId
      }
    },
    {
      skip: !divisionId || !open
    }
  );
  const { data: divisionAccountingSettings } = useGetTenantAccountingSettingsQuery(
    {
      divisionId: divisionId
    },
    {
      skip: !divisionId
    }
  );
  const [updateClient] = useUpdateCustomerMutation();
  const { data: qboPreferences = { classTrackingPerTxn: false } } = useGetQBOClassTrackingQuery(
    divisionId,
    { skip: !divisionId }
  );
  const { data: divisionClasses = { data: [], size: 0 } } = useGetQBOClassesByDivisionQuery(
    divisionId,
    { skip: !divisionId }
  );
  const [getOrdersForInvoice] = useGetOrdersForInvoiceMutation();

  const classesDropdown: TwSelectOption[] = [
    { label: 'No Class', value: '' },
    ...divisionClasses.data.map((c) => ({
      label: c.Name,
      value: c.Id
    }))
  ];

  // #region settings
  const isEnabledDriversSeeDocs = getTenantSetting('allow_drivers_to_view_documents');
  const includeNoticeOfAssignmentEnabled = getTenantSetting(
    'enable-factoring-notice-of-assignment'
  );

  // internal factoring company
  const { data: fetchedInternalFactoring } = useGetInternalFactoringCompanyQuery(
    { divisionId },
    { skip: !divisionId }
  );

  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  // #region states
  const [isFirstTime, setIsFirstTime] = useState(true);
  const [showQBOLoader, setShowQBOLoader] = useState<boolean>(false);
  const [haveEmail, setHaveEmail] = useState(false);
  const [oldClientEmail, setOldClientEmail] = useState('');
  const [clientEmailMissing, setClientEmailMissing] = useState(false);
  const [emailsToSend, setEmailsToSend] = useState<string[]>([]);
  const [emailsToCc, setEmailsToCc] = useState<string[]>([]);
  const [maxAttachmentSize, setMaxAttachmentSize] = useState<number>(35);
  const [emailsToSendQBO, setEmailsToSendQBO] = useState<string[]>([]);
  const [additionalQBOEmails, setAdditionalQBOEmails] = useState<string[]>([]);
  const [suggestedEmails, setSuggestedEmails] = useState<TwTagsOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [initialInvoiceData, setInitialInvoiceData] =
    useState<EditInvoiceFormValues>(INITIAL_INVOICE_DATA);
  const [clientData, setClientData] = useState<CreateClientData>();
  const [isOpenWarning, setIsOpenWarning] = useState(false);
  const [docsViewerOpen, setDocsViewerOpen] = useState(false);
  const [fileToView, setFileToView] = useState<OrderAttachment>({} as OrderAttachment);
  const [readableId, setReadableId] = useState<number>();
  const [originalInvoice, setOriginalInvoice] = useState<InvoiceResponseV3>();
  const [singleLoad, setSingleLoad] = useState<Order>();
  const [source, setSource] = useState<InvoiceSources | undefined>();
  const [loadsRates, setLoadsRates] = useState<{ [key: string]: RowData[] } | undefined>(undefined);
  const [loads, setLoads] = useState<Order[]>([]);
  const [qboServicesList, setQboServicesList] = useState<TwAutocompleteOption[]>([]);
  const [billFromData, setBillFromData] = useState<BillFromData | undefined>();
  const [billToData, setBillToData] = useState<BillToData | undefined>();
  const [billFromLocationId, setBillFromLocationId] = useState<string | undefined>();
  const [billToLocationId, setBillToLocationId] = useState<string | undefined>();
  const [billFromCompanyIds, setBillFromCompanyIds] = useState<string[]>([]);
  const [billFromSelectedCompanyId, setBillFromSelectedCompanyId] = useState<string | undefined>();
  const [tempBillFromSelectedCompanyId, setTempBillFromSelectedCompanyId] = useState<
    string | undefined
  >();
  const [carriers, setCarriers] = useState<CarrierResponse[]>([]);
  const [previewBlob, setPreviewBlob] = useState<Blob>();
  const [previewBlobTitle, setPreviewBlobTitle] = useState<string>('');
  const [previewDownloadName, setPreviewDownloadName] = useState<string>('');
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
  const [invoicePreviewLoading, setInvoicePreviewLoading] = useState(false);
  const [invoicePreviewAttachments, setInvoicePreviewAttachments] = useState<OrderAttachment[]>([]);
  const [invoiceClient, setInvoiceClient] = useState<ClientResponse | undefined>(undefined);
  const [dueDate, setDueDate] = useState<Date | undefined>();
  const [documentsOpen, setDocumentsOpen] = useState<boolean>(false);
  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([]);
  const [attachmentSizeWarningOpen, setAttachmentSizeWarningOpen] = useState<boolean>(false);
  const [zipAttachmentSizeWarningOpen, setZipAttachmentSizeWarningOpen] = useState<boolean>(false);
  const [showFactorInvoice, setShowFactorInvoice] = useState(false);
  const [factoringCompanyName, setFactoringCompanyName] = useState<string | null>(null);
  const [factorInvoice, setFactorInvoice] = useState(false);

  // #region handlers
  const handleSelectedEmailsToSend = (emails: string[]): void => {
    setEmailsToSend(Array.from(new Set(emails)));
  };

  const handleSelectedEmailsToCc = (emails: string[]): void => {
    setEmailsToCc(Array.from(new Set(emails)));
  };

  const handlePreviewInvoiceClose = (doCloseInvoicePage: boolean): void => {
    setIsFirstTime(true);
    setInvoicePreviewOpen(false);
    setEditInvoiceOpen(!doCloseInvoicePage);
  };

  const handleClose = (): void => {
    setEditInvoiceOpen(false);
    dispatch(setOrderId(undefined));
    dispatch(setInvoiceLoadIds([]));
    dispatch(setInvoiceDivisionId(''));
    setSource(InvoiceSources.TB);
    setDueDate(getDate30DaysFromNow());
    setEmailsToSend([]);
    setEmailsToCc([]);
    setAdditionalQBOEmails([]);
    setSelectedDocs([]);
    setSelectedPhotos([]);
    setInvoicePreviewAttachments([]);
    setIsOpenWarning(false);
    setPreviewBlob(undefined);
    setClientEmailMissing(false);
    setSingleLoad(undefined);
    setOriginalInvoice(undefined);
    setInitialInvoiceData(INITIAL_INVOICE_DATA);
    setShowQBOLoader(false);
    onClickClose();
  };

  const handleSelectedAdditionalEmails = (emails: string[]): void => {
    setAdditionalQBOEmails(emails);
  };
  const handleDocumentsUpload = async (
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ): Promise<void> => {
    if (!singleLoad) return;
    try {
      const allDocs = await LoadsService.getAllDocumentsByOrderOrTrip([singleLoad.id as string]);

      if (allDocs && allDocs.length > 0) {
        const invoicePhotos = allDocs.filter(
          (doc) =>
            doc.attachment.file_type.includes('image') && (!doc.type || LoadDocumentType.empty)
        );
        const onlyDocs = allDocs.filter(
          (doc) =>
            doc.type !== LoadDocumentType.empty || !doc.attachment.file_type.includes('image')
        );
        setSingleLoad({
          ...(singleLoad as Order)
        });
        setFieldValue('documents', onlyDocs);
        setFieldValue('photos', invoicePhotos);
      }
    } catch (error) {
      addNotification({
        variant: 'warning',
        message:
          'Documents uploaded but failed to fetch them. Please refresh the page to see the documents.'
      });
    }
  };

  const getFactoringConfig = async (): Promise<void> => {
    FactoringService.getConfig(divisionId)
      .then((response) => {
        setFactoringCompanyName(response.company);
        setShowFactorInvoice(true);
      })
      .catch((error) => {
        if (error.status !== 404 && error.status !== 403) {
          addNotification({
            message: error.data
              ? error.data
              : "Something went wrong while getting factoring company's config",
            variant: 'error'
          });
        }

        if (error.status === 404 && fetchedInternalFactoring) {
          setFactoringCompanyName(fetchedInternalFactoring.name);
          setShowFactorInvoice(true);
          return;
        }

        setShowFactorInvoice(false);
      });
  };

  const handleSend = (
    values: EditInvoiceFormValues,
    previewData?: PreviewInvoiceSendData
  ): void => {
    if (isBillFromChanged) {
      addNotification({
        message: `Save Billing information to proceed`,
        variant: 'error'
      });
      return;
    }

    if (source === InvoiceSources.TB && values.sendInvoice) {
      previewInvoice(values);
    } else {
      const canSaveOrSend = canSaveAndOrSendInvoice(values);
      if (!canSaveOrSend) return; // want to show these error notifications closing the modal

      if (source === InvoiceSources.QBO && !enableSendQboInvoiceInternally) {
        if (additionalQBOEmails.length <= 0 && values.sendInvoice && emailsToSendQBO.length <= 0) {
          addNotification({
            message: 'Please fill in the Additional recipients field with at least one email',
            variant: 'error'
          });
          return;
        }
      }

      if (!(source === InvoiceSources.QBO && enableSendQboInvoiceInternally)) {
        setEditInvoiceOpen(false);
      }

      sendInvoice(values, previewData);
    }
  };

  // #region functions
  const getDate30DaysFromNow = (): Date => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date;
  };

  const updatePresetSendToEmails = (
    billingEmail?: string,
    contactEmail?: string,
    preferences?: Preferences
  ): void => {
    const addCustomerEmailToSend =
      (source === InvoiceSources.TB &&
        (toCustomerBilling || (toCustomerContact && !contactEmail))) ||
      source === InvoiceSources.QBO;

    // Set default emailsToSend
    const existingEmails = [...emailsToSend];
    const newListToSend = existingEmails.filter((email) => email !== oldClientEmail);

    if (addCustomerEmailToSend && billingEmail) {
      newListToSend.push(billingEmail);
    }

    if (toCustomerContact && contactEmail) {
      newListToSend.push(contactEmail);
    }
    // Set default emailsToCc for Truckbase invoice Preview
    const listToCc = [...emailsToCc];

    if (ccTenantAccounting && accountingSettings?.accounting_email) {
      listToCc.push(accountingSettings.accounting_email);
    }

    if (ccTenantEmail && userData?.email_address) {
      listToCc.push(userData.email_address);
    }
    // if customer has preferences, add additional recipients from customer preferences
    if (preferences) {
      const emailsFromPreferences = preferences?.invoice_settings?.additional_invoice_recipients;

      emailsFromPreferences &&
        emailsFromPreferences.forEach((email) => {
          listToCc.push(email);
        });
    } else {
      // if customer has NO preferences, add additional recipients from global division accounting settings

      if (accountingSettings && accountingSettings.additional_recipients) {
        accountingSettings.additional_recipients.forEach((email) => {
          listToCc.push(email);
        });
      }
    }

    // TB recipients
    setEmailsToSend(newListToSend);
    setEmailsToCc(Array.from(new Set(listToCc)));
    // QBO recipients
    billingEmail && setEmailsToSendQBO([billingEmail]); // Only QBO will use just customer billing email always (regardless of settings)
    if (toCustomerContact && contactEmail) {
      setAdditionalQBOEmails((prev) => [...listToCc, contactEmail]);
    } else {
      setAdditionalQBOEmails(listToCc);
    }
  };

  const checkEmail = (invoiceClient: ClientResponse): void => {
    const hasEmail = !!invoiceClient.billing_email;
    setOldClientEmail(invoiceClient.billing_email || '');
    let toCustomerContact;
    if (invoiceClient.contacts && invoiceClient.contacts.length > 0) {
      toCustomerContact =
        invoiceClient.contact?.email_address ?? invoiceClient.contacts[0]?.email_address;
    }

    updatePresetSendToEmails(
      invoiceClient.billing_email,
      toCustomerContact,
      invoiceClient.preferences || undefined
    );
    setHaveEmail(hasEmail);
    setClientData({
      companyName: invoiceClient.name,
      companyId: invoiceClient.id,
      billingEmail: invoiceClient.billing_email
    });
  };

  const checkOutdatedInfo = (loads: Order[]): InvoiceItemV3[] => {
    const getInvoiceDefaultDescription = invoiceDefaultDescriptionMaker(accountingSettings!, commoditiesEnabled);
    const loadsOutOfSync: Order[] = loads.filter(
      (l) => l.invoice_order_sync_state === InvoiceState.INVOICE_OUTDATED
    );
    let updatedLoadsItems: InvoiceItemV3[] = [];

    if (loadsOutOfSync.length > 0) {
      loadsOutOfSync.map((load) => {
        if (load.order_rates && load.order_rates.length > 0) {
          // if advanced load rates present
          const updatedLoadItems = load.order_rates.map((rate, servicesIndex) => {
            return {
              amount: NumberUtils.calculateCurrencyAmount(rate.rate, rate.quantity),
              description: rate.sort_order === 1 ? getInvoiceDefaultDescription(load) : '',
              invoice_service_id: rate.invoice_service_id,
              invoice_service_name: rate.service_name,
              qbo_service_id: rate.qbo_service_id,
              order_id: load.id,
              quantity: rate.quantity,
              rate: rate.rate || 0,
              unit: rate.unit || undefined,
              sort_order: rate.sort_order,
              ui_id: nanoid(),
              qbo_attributes: rate.qbo_attributes
            };
          });

          updatedLoadsItems = [...updatedLoadsItems, ...updatedLoadItems];
        } else {
          // if simple rate present
          // set default service
          let service = invoiceServicesData?.content?.find((service) => service.isDefault);
          // if not default service
          if (!service) {
            if (!quickbooksEnabled) {
              service = invoiceServicesData?.content?.find(
                (service) => service.name === 'Linehaul flat rate'
              );
            } else {
              service = invoiceServicesData?.content?.find(
                (service) => service.name === 'Services'
              );
            }
          }

          if (service) {
            const updatedLoadItems = [
              {
                amount: NumberUtils.calculateCurrencyAmount(load.rate, 1),
                description: getInvoiceDefaultDescription(load),
                invoice_service_id: service.id,
                invoice_service_name: service.name,
                qbo_service_id: service.id,
                order_id: load.id,
                quantity: 1,
                rate: load.rate || 0,
                unit: service.unit,
                sort_order: 1,
                ui_id: nanoid()
              }
            ];
            updatedLoadsItems = [...updatedLoadsItems, ...updatedLoadItems];
          }
        }
      }) || [];
    }

    return updatedLoadsItems;
  };

  const getData = useCallback(async () => {
    try {
      if (!invoiceId || !open || !accountingSettings || !invoiceServicesData) return;
      const originalInvoiceResponse = await InvoicesService.getInvoice(invoiceId);
      if (originalInvoiceResponse.data) {
        const originalInvoice = originalInvoiceResponse.data;
        const loadsRatesMap: { [key: string]: RowData[] } = {};
        setOriginalInvoice(originalInvoice);
        setSource(originalInvoice.source as InvoiceSources);
        let invoiceLines = originalInvoice.invoice_items;

        // only going to populate these two fields if there is one load associated with the invoice
        let invoicePhotos: OrderAttachment[] = [];
        let onlyDocs: OrderAttachment[] = [];

        if (originalInvoice.bill_from_company_id) {
          setBillFromSelectedCompanyId(originalInvoice.bill_from_company_id);
          setTempBillFromSelectedCompanyId(originalInvoice.bill_from_company_id);
        }

        // loop through originalInvoice.loads and make a list of loadIds
        const orderIds = originalInvoice.orders.map((order) => order.id);

        const loads = await getOrdersForInvoice(orderIds).unwrap();
        setLoads(loads);
        const allDocs = await LoadsService.getAllDocumentsByOrderOrTrip(orderIds);

        if (allDocs && allDocs.length > 0) {
          invoicePhotos = allDocs.filter(
            (doc) =>
              doc.attachment.file_type.includes('image') && (!doc.type || LoadDocumentType.empty)
          );
          onlyDocs = allDocs.filter(
            (doc) =>
              doc.type !== LoadDocumentType.empty || !doc.attachment.file_type.includes('image')
          );

          invoicePhotos = await FileUtils.getPhotosInBatch(invoicePhotos, 5);
        }

        // if invoice only has one load associated with it, add load info
        if (originalInvoice.orders.length === 1) {
          const load = loads[0];
          if (load) {
            setReadableId(load.readable_id);
            setSingleLoad(load);

            // Set customer contacts emails to use as suggestions
            setSuggestedEmails(
              getCustomerContactEmails(
                load.raw_customer as ClientResponse,
                includeNoticeOfAssignmentEnabled,
                userData?.factoringCompany
              )
            );

            checkEmail(load.raw_customer || ({} as ClientResponse));
            setInvoiceClient(load.raw_customer || ({} as ClientResponse));
          }
        } else {
          checkEmail(originalInvoice.client);
          setInvoiceClient(originalInvoice.client);
        }

        setPreviewBlobTitle(InvoiceUtils.getInvoiceBlobTitle(originalInvoice.invoice_id || ''));
        setPreviewDownloadName(
          InvoiceUtils.getInvoiceDownloadName(originalInvoice.invoice_id || '')
        );
        // check for outdated info
        const invoiceHasOutdatedInfo = checkOutdatedInfo(loads);

        if (invoiceHasOutdatedInfo.length > 0) {
          if (loads.length > 1) {
            const serviceIdToUse = quickbooksEnabled ? 'qbo_service_id' : 'invoice_service_id';
            const originalInvoiceLines = invoiceLines.filter(
              (line) =>
                !invoiceHasOutdatedInfo.some(
                  (outdated) =>
                    outdated.order_id === line.order_id &&
                    outdated[serviceIdToUse] === line[serviceIdToUse]
                )
            );

            invoiceLines = [...invoiceHasOutdatedInfo, ...originalInvoiceLines];
            const loadsOutOfSync = loads.filter(
              (l) => l.invoice_order_sync_state === InvoiceState.INVOICE_OUTDATED
            );
            const updatedLoads = loadsOutOfSync
              .map((load) => `Order #${load.readable_id}`)
              .join(', ');
            addNotification({
              message: `${updatedLoads} has been updated with the latest rate information`,
              variant: 'success'
            });
          } else {
            invoiceLines = invoiceHasOutdatedInfo;
            addNotification({
              message: `Order #${loads[0].readable_id} has been updated with the latest rate information`,
              variant: 'success'
            });
          }
        }

        setInitialInvoiceData({
          ...initialInvoiceData,
          invoiceNumber: originalInvoice.invoice_id || '',
          source: originalInvoice.source as InvoiceSources,
          sendInvoice: quickbooksEnabled
            ? false
            : accountingSettings.select_invoice_send_option !== undefined
            ? accountingSettings.select_invoice_send_option
            : true, // Use tenant's setting if set, otherwise default to true only if for TB invoices,
          photos: invoicePhotos, // only setting photos and documents if there is one load
          documents: onlyDocs,
          // Just to calm down TS
          // By the point in time this is executed there will be a source already...
          invoiceItems: [...(invoiceLines || [])],
          qboClassId: originalInvoice.qbo_attributes?.qbo_class_ref?.value ?? '',
          salesTaxId: originalInvoice.sales_tax_id ?? '',
          salesTaxRate: originalInvoice.sales_tax_rate ?? 0
        });

        invoiceLines
          .sort(
            (i1: InvoiceItemV3, i2: InvoiceItemV3) =>
              (i1.sort_order || originalInvoiceResponse.data.invoice_items.length) -
              (i2.sort_order || originalInvoiceResponse.data.invoice_items.length)
          )
          .forEach((item) => {
            if (item.order_id) {
              // we check if the service coming from the load is present in the invoice services
              // E.g Creating an invoice for a load with Truckbase services while connected to QBO
              const service = invoiceServicesData?.content?.find((service) =>
                quickbooksEnabled
                  ? service.id === item.qbo_service_id
                  : service.id === item.invoice_service_id
              );

              const isServiceOk = !!service;
              loadsRatesMap[item.order_id] = loadsRatesMap[item.order_id] || [];
              loadsRatesMap[item.order_id].push({
                description: item.description,
                invoice_service_id: isServiceOk ? item.invoice_service_id : undefined,
                qbo_service_id: isServiceOk ? item.qbo_service_id : undefined,
                rate: item.rate || 0,
                service_name: isServiceOk ? item.invoice_service_name : '',
                unit: item.unit,
                quantity: item.quantity,
                amount: (item.rate ?? 0) * (item.quantity ?? 0),
                ui_id: nanoid(),
                load_id: item.order_id,
                initial_service_option: {
                  id: quickbooksEnabled ? item.qbo_service_id || '' : item.invoice_service_id || '',
                  label: isServiceOk ? item.invoice_service_name || '' : '',
                  value: {
                    id: quickbooksEnabled ? item.qbo_service_id : item.invoice_service_id,
                    name: isServiceOk ? item.invoice_service_name : '',
                    default_rate: 0,
                    unit: item.unit
                  } as InvoiceService
                },
                qbo_attributes: item.qbo_attributes
              });
            }
          });

        setLoadsRates(loadsRatesMap);
        setDueDate(
          originalInvoice.due_date
            ? DateUtils.getDateInLocale(new Date(originalInvoice.due_date))
            : getDate30DaysFromNow()
        );

        setIsFirstTime(false);
      }
    } catch (error) {
      console.log('Error getting invoice order data', error);
      // TODO make this a toast
      alert("Couldn't get info");
    } finally {
      setIsLoading(false);
    }
  }, [invoiceId, open, accountingSettings, invoiceServicesData]);

  const getButtonLabel = useCallback(
    (values: EditInvoiceFormValues): string => {
      if (source === InvoiceSources.TB) {
        return values.sendInvoice ? 'Preview' : 'Save';
      }

      return enableSendQboInvoiceInternally ? 'Save + preview' : 'Save';
    },
    [source, enableSendQboInvoiceInternally]
  );

  const saveBillFromInfo = (billFromData: BillFromData): void => {
    setBillFromData(billFromData);
  };

  const saveBillToInfo = (billToData: BillToData): void => {
    setBillToData(billToData);
    if (
      emailsToSend &&
      !emailsToSend.includes(billToData.clientEmail) &&
      StringUtils.isValidEmail(billToData.clientEmail)
    ) {
      updatePresetSendToEmails(
        billToData.clientEmail,
        invoiceClient?.contact?.email_address,
        billToData.preferences || undefined
      );
    }
  };

  const previewInvoice = async (values: EditInvoiceFormValues): Promise<void> => {
    const finalInvoiceItems = values.invoiceItems.map((item) => {
      const finalItem: InvoiceRateItem = {
        description: item.description,
        amount: Number(item.amount) || 0
      };
      item.product ? (finalItem.product = item.product.id) : null;
      return finalItem;
    });

    const invoiceItems: InvoiceItemV3[] = loadsRates
      ? Object.keys(loadsRates)
          .map((loadId) => {
            const loadRates = loadsRates[loadId];
            return loadRates.map((rate, index) => ({
              description: rate.description || '',
              amount: rate.amount || 0,
              invoice_service_id: rate.invoice_service_id,
              invoice_service_name: rate.service_name || '',
              unit: rate.unit || undefined,
              rate: rate.rate || undefined,
              quantity: rate.quantity,
              order_id: loadId,
              sort_order: index + 1
            }));
          })
          .flat()
      : [];

    const checkedPhotos = selectedPhotos.map(
      (photo) => values.photos.find((value) => value.id === photo) || ({} as OrderAttachment)
    );

    const checkedDocuments = selectedDocs.map(
      (doc) => values.documents.find((value) => value.id === doc) || ({} as OrderAttachment)
    );

    const totalPhotosSize = checkedPhotos.reduce((acc, ph) => acc + ph.attachment.size, 0);
    const totalDocsSize = checkedDocuments.reduce((acc, doc) => acc + doc.attachment.size, 0);

    const totalFilesSize = totalDocsSize + totalPhotosSize;

    if (totalFilesSize >= maxAttachmentSize * 1024 * 1024) {
      // 35 MB if not zipped, 70 MB if zipped
      if (zipAttachments) {
        setZipAttachmentSizeWarningOpen(true);
        return;
      } else {
        setAttachmentSizeWarningOpen(true);
        return;
      }
    }

    setInvoicePreviewAttachments([...checkedPhotos, ...checkedDocuments]);
    setInvoicePreviewOpen(true);
    setInvoicePreviewLoading(true);
    try {
      const invoice: InvoiceUpdateDataV3 | InvoiceUpdateData = {
        invoiceNumber: originalInvoice?.invoice_number || undefined,
        invoiceId: originalInvoice?.invoice_id || undefined,
        source: source,
        orderIds: originalInvoice?.orders.map((load) => load.id) ?? [],
        clientId: originalInvoice?.client.id,
        sendInvoice: values.sendInvoice,
        sendTo: emailsToSend ? emailsToSend : [],
        invoiceItems: originalInvoice?.is_legacy ? finalInvoiceItems : invoiceItems,
        invoiceAttachments: selectedDocs,
        invoicePhotos: selectedPhotos,
        tenantCC: values.tenantCcInvoice,
        specialInstructions: values.specialInstructions,
        dueDate: dueDate?.toISOString(),
        mergeAttachments: mergeAttachments,
        zipAttachments: zipAttachments,
        billFromCompanyId: billFromSelectedCompanyId,
        divisionId: divisionId
      };
      // Generate preview from invoice

      const subTotal = loadsRates ? getTotal(loadsRates) : 0;
      const salesTaxAmount = totalWithTax - subTotal;
      const salesTaxRate = salesTax?.value || 0;
      const salesTaxName = salesTax?.label || '';

      const invoicePreviewData: InvoicePreviewPdfData = {
        invoice: invoice,
        salesTaxEnabled: isSalesTaxEnabled,
        salesTaxRate: salesTaxRate,
        subtotal: subTotal,
        salesTaxAmount: salesTaxAmount,
        amount: totalWithTax,
        salesTaxName: salesTaxName
      };

      const invoicePreviewResponse = originalInvoice?.is_legacy
        ? await InvoicesService.generateInvoicePreviewV2(invoice as InvoiceUpdateData)
        : await InvoicesService.generateInvoicePreviewV3(invoicePreviewData);

      // Set blob of invoice preview
      invoicePreviewResponse && setPreviewBlob(invoicePreviewResponse);
      setIsShowing(false);
    } catch (err: any) {
      setIsShowing(false);
      console.log('Error generating invoice preview', err);
      addNotification({
        message: `Couldn't generate preview`,
        variant: 'error',
        actionOptions: {
          action: 'RETRY',
          onActionClick: (): void => {
            sendInvoice(values);
          }
        },
        timeToShow: 5000,
        onClosed: () => setIsFirstTime(true)
      });
    } finally {
      // After Truckbase invoice preview we need to go be able to come Back so we don't close Create dialog
      setInvoicePreviewLoading(false);
    }
  };

  const canSaveAndOrSendInvoice = (formValues: EditInvoiceFormValues): boolean => {
    const checkedPhotos = selectedPhotos.map(
      (photo) => formValues.photos.find((value) => value.id === photo) || ({} as OrderAttachment)
    );
    const totalPhotosSize = checkedPhotos.reduce((acc, ph) => acc + ph.attachment.size, 0);

    const checkedDocuments = selectedDocs.map(
      (doc) => formValues.documents.find((value) => value.id === doc) || ({} as OrderAttachment)
    );
    const totalDocsSize = checkedDocuments.reduce((acc, doc) => acc + doc.attachment.size, 0);

    const totalFilesSize = totalDocsSize + totalPhotosSize;

    if (totalFilesSize >= maxAttachmentSize * 1024 * 1024) {
      // 35 MB if not zipped, 70 MB if zipped
      if (zipAttachments) {
        setZipAttachmentSizeWarningOpen(true);
        return false;
      } else {
        setAttachmentSizeWarningOpen(true);
        return false;
      }
    }

    if (source === InvoiceSources.TB && !billFromData?.companyName) {
      logRocketTrack('Invoice', {
        action: 'Edit bill from info',
        severity: 'low',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: `Please fill in your company name.`,
        variant: 'error',
        timeToShow: 7000
      });

      return false;
    }

    return true;
  };

  const sendInvoice = async (
    values: EditInvoiceFormValues,
    previewData?: PreviewInvoiceSendData,
    forceSend?: boolean
  ): Promise<void> => {
    setClientEmailMissing(false);

    const finalInvoiceItems = values.invoiceItems.map((item) => {
      const finalItem: InvoiceRateItem = {
        description: item.description,
        amount: item.amount || 0
      };
      item.product ? (finalItem.product = item.product.id) : null;
      return finalItem;
    });
    const invoiceItems: InvoiceItemV3[] = loadsRates
      ? Object.keys(loadsRates)
          .map((loadId) => {
            const loadRates = loadsRates[loadId];
            return loadRates.map((rate, index) => {
              const itemObj: InvoiceItemV3 = {
                description: rate.description || '',
                amount: rate.amount || 0,
                invoice_service_name: rate.service_name,
                unit: rate.unit || undefined,
                rate: rate.rate || undefined,
                quantity: rate.quantity,
                order_id: loadId,
                sort_order: index + 1
              };

              if (quickbooksEnabled) {
                itemObj.qbo_service_id = rate.qbo_service_id;
                itemObj.qbo_attributes = rate.qbo_attributes;
              } else {
                itemObj.invoice_service_id = rate.invoice_service_id;
                itemObj.invoice_service_name = rate.service_name;
              }

              return itemObj;
            });
          })
          .flat()
      : [];

    const canSend = canSaveAndOrSendInvoice(values);
    if (!canSend) return;

    addNotification({
      message: 'Invoice is being updated...',
      variant: 'loading',
      timeToShow: 10000
    });

    let skipFinally = false;

    try {
      // we need to update email client first (for qbo invoice)
      if (!haveEmail && values.companyEmail) {
        const params = {
          ...clientData,
          externalCustomerId: clientData?.externalCustomerId,
          companyId: clientData?.companyId,
          companyEmail: values.companyEmail,
          companyName: clientData?.companyName ?? '',
          currencyPreference: clientData?.currencyPreference,
          mcNumber: clientData?.mcNumber,
        };

        await updateClient(params)
          .unwrap()
          .catch((error) => {
            console.error(error);
            logRocketTrack('Invoice', {
              action: 'Update customer info',
              severity: 'high',
              tenant_id: userData?.tenant?.id,
              company_name: userData?.tenant?.company_name,
              email: userData?.email_address,
              user_first_name: userData?.first_name,
              user_last_name: userData?.last_name
            });
            addNotification({
              message: 'Error at updating customer info, please try again',
              variant: 'error'
            });
          });
      }

      // If "Bill to" data was edited, we create location (if not created manually)
      // and update rest of client info (for truckbase invoice)
      if (billToData) {
        const clientUpdateData: CreateClientData = {
          ...billToData,
          externalCustomerId: billToData.externalCustomerId,
          companyName: billToData.clientName,
          billingEmail: billToData.clientEmail,
          currencyPreference: billToData?.currencyPreference,
          mcNumber: billToData?.mcNumber
        };

        billToData.clientPhone ? (clientUpdateData.primaryPhone = billToData.clientPhone) : null;

        if (billToLocationId) {
          clientUpdateData.locationId = billToLocationId;
        } else if (billToData.placeName && billToData.addressLine) {
          const newClientLocation: CreateLocationData = {
            name: billToData.placeName,
            address_1: billToData.addressLine,
            city: billToData.city || '',
            zip: billToData.zip || '',
            state: billToData.state || ''
          };

          if (billToData.placeId !== '') {
            newClientLocation.google_place_id = billToData.placeId;
          }

          try {
            const locationResponse = await LoadsService.createLocation(newClientLocation);
            if (locationResponse) {
              clientUpdateData.locationId = locationResponse.id;
            }
          } catch (error) {
            console.error(error);
          }
        }

        const params = {
          ...clientUpdateData,
          externalCustomerId: clientUpdateData.externalCustomerId,
          companyId: billToData.clientId
        };
        await updateClient(params)
          .unwrap()
          .catch((error) => {
            console.error(error);
            logRocketTrack('Invoice', {
              action: 'Update customer info',
              severity: 'high',
              tenant_id: userData?.tenant?.id,
              company_name: userData?.tenant?.company_name,
              email: userData?.email_address,
              user_first_name: userData?.first_name,
              user_last_name: userData?.last_name
            });
            addNotification({
              message: 'Error at updating customer info, please try again',
              variant: 'error'
            });
          });
      }

      // If "Bill from" data was edited, we create location (if not created manually)
      // and update rest of tenant info (for truckbase invoice)
      if (billFromData && source === InvoiceSources.TB) {
        const tenantUpdateData: AccountData = {
          company_name: billFromData.companyName
        };

        billFromData.companyPhone ? (tenantUpdateData.phone = billFromData.companyPhone) : null;

        if (billFromLocationId) {
          tenantUpdateData.location_id = billFromLocationId;
        } else if (billFromData.placeName && billFromData.addressLine) {
          const newTenantLocation: CreateLocationData = {
            name: billFromData.placeName,
            address_1: billFromData.addressLine,
            city: billFromData.city || '',
            zip: billFromData.zip || '',
            state: billFromData.state || ''
          };

          if (billFromData.placeId !== '') {
            newTenantLocation.google_place_id = billFromData.placeId;
          }

          try {
            const locationResponse = await LoadsService.createLocation(newTenantLocation);
            if (locationResponse) {
              tenantUpdateData.location_id = locationResponse.id;
            }
          } catch (error) {
            console.error(error);
          }
        }

        await SettingsService.updateAccount(tenantUpdateData);
      }

      try {
        if (source === InvoiceSources.QBO && enableSendQboInvoiceInternally && !forceSend) {
          setShowQBOLoader(true);
        }

        const invoice: InvoiceUpdateDataV3 = {
          sendInvoice:
            source === InvoiceSources.QBO && enableSendQboInvoiceInternally
              ? !!forceSend
              : values.sendInvoice,
          sendTo:
            source === InvoiceSources.QBO && !enableSendQboInvoiceInternally
              ? emailsToSendQBO
              : emailsToSend ?? [],
          ccList: source === InvoiceSources.QBO ? additionalQBOEmails : emailsToCc ?? [],
          invoiceItems: originalInvoice?.is_legacy ? finalInvoiceItems : invoiceItems,
          invoiceAttachments: selectedDocs,
          invoicePhotos: selectedPhotos,
          tenantCC: values.tenantCcInvoice,
          specialInstructions: values.specialInstructions,
          dueDate: dueDate?.toISOString(),
          mergeAttachments: mergeAttachments,
          zipAttachments: zipAttachments,
          billFromCompanyId: billFromSelectedCompanyId ? billFromSelectedCompanyId : null,
          divisionId: divisionId,
          // Sending invoice for QBO from preview page so invoice with all latest attachments is already created so no need to update attachments again
          skipAttachmentUpdates:
            source === InvoiceSources.QBO && enableSendQboInvoiceInternally && !!forceSend
              ? true
              : false,
          salesTaxId: salesTax?.id,
          salesTaxRate: salesTax?.value
        };
        if (values.qboClassId) {
          const classList = divisionClasses.data;
          const index = classList.findIndex((c) => c.Id === values.qboClassId);
          invoice.qboClassRef = {
            value: classList[index].Id,
            name: classList[index].Name
          };
        }
        if (previewData) {
          invoice.emailSubject = previewData.emailSubject;
          invoice.emailBody = previewData.emailBody;
        }

        // update invoice
        const invoiceResponse = await InvoicesService.updateInvoice(invoiceId || '', invoice);

        try {
          // send to factoring company
          let factoredInvoice = null;

          if (factorInvoice && loads.length === 1) {
            // get updated invoice
            const updatedInvoice = (await InvoicesService.getInvoice(invoiceId)).data;
            factoredInvoice = await FactoringService.postExport(
              updatedInvoice as InvoiceResponseV3
            );
          }

          if (factorInvoice && factoredInvoice) {
            addNotification({
              message: `Invoice(s) sent to ${FactoringUtils.capitalizeFirstLetterCompany(
                factoringCompanyName || undefined
              )} for factoring`,
              variant: 'success',
              timeToShow: 5000,
              onClosed: () => setIsFirstTime(true)
            });
          }
        } catch (error) {
          console.error(error);
          addNotification({
            message: 'Error at sending invoice to factoring company',
            variant: 'error',
            timeToShow: 5000,
            onClosed: () => setIsFirstTime(true)
          });
        }

        setReloadInvoices && setReloadInvoices((reloadInvoices) => !reloadInvoices);

        // get blob of invoice
        const invoiceBlob = await InvoicesService.getInvoicePdf(invoiceId);
        setViewInvoiceBlob(invoiceBlob);

        if (source === InvoiceSources.QBO && enableSendQboInvoiceInternally && !forceSend) {
          skipFinally = true;
          setInvoicePreviewOpen(true);
          setInvoicePreviewLoading(true);
          setPreviewBlob(invoiceBlob);
          setIsShowing(false);
          setInvoicePreviewLoading(false);
          setShowQBOLoader(false);
          return;
        }

        setEditInvoiceOpen(false);
        // refresh another components if necessary
        dispatch(setLoadDetailReload(true));

        if (invoiceResponse) {
          // if invoiceResponse does not start with a 2 or isn't 409 then let's do a generic message
          let message = invoiceResponse.message;
          if (
            !invoiceResponse.status?.toString().startsWith('2') &&
            invoiceResponse.status !== 409
          ) {
            console.error(
              'Error updating invoice. Contact support@truckbase.com.',
              invoiceResponse
            );
            message = 'Error updating invoice.';
          }

          addNotification({
            message: message,
            variant: invoiceResponse.status === 206 ? 'error' : 'success',
            timeToShow: 5000,
            onClosed: () => setIsFirstTime(true),
            actionOptions: {
              action: 'open',
              onActionClick: (): void => {
                // Opens invoice PDF preview
                setViewInvoiceOpen(true);
              }
            }
          });
        }
      } catch (err: any) {
        console.error('Invoice error', err);
        logRocketTrack('Invoice', {
          action: 'can be a number of issues',
          severity: 'high',
          tenant_id: userData?.tenant?.id,
          company_name: userData?.tenant?.company_name,
          email: userData?.email_address,
          user_first_name: userData?.first_name,
          user_last_name: userData?.last_name
        });

        // if err message contains 'Quickbooks' then show that else show generic message
        const errorMessage = err.message.includes('QuickBooks')
          ? err.message
          : 'Invoice error. Contact support@truckbase.com.';

        addNotification({
          message: errorMessage,
          variant: 'error',
          actionOptions: {
            action: 'RETRY',
            onActionClick: (): void => {
              sendInvoice(values);
            }
          },
          timeToShow: 5000,
          onClosed: () => setIsFirstTime(true)
        });
        setShowQBOLoader(false);
      }
    } catch (err: any) {
      console.error('Invoice error', err);
      addNotification({
        message: 'Invoice error. Contact support@truckbase.com.',
        variant: 'error',
        actionOptions: {
          action: 'RETRY',
          onActionClick: (): void => {
            sendInvoice(values);
          }
        },
        timeToShow: 5000,
        onClosed: () => setIsFirstTime(true)
      });
      setShowQBOLoader(false);
    } finally {
      if (skipFinally) return;

      dispatch(setOrderId(undefined));
      handlePreviewInvoiceClose(true);
      handleClose();
    }
  };

  const closeWarning = (): void => {
    setIsFirstTime(true);
    setIsOpenWarning(false);
  };

  const validate = (values: FormikValues): void | FormikErrors<FormikValues> => {
    const [errors, errorInputName] = getErrorInfo(loadsRates, source);

    if (errorInputName) {
      const errorMessages = Object.values(errors);
      if (errorMessages && errorMessages.length > 0) {
        addNotification({
          message: errorMessages[0] as string,
          variant: 'error'
        });
      }
      scrollToInput(errorInputName);
    }
    return errors;
  };

  const { values, handleChange, handleSubmit, setFieldValue } = useFormik({
    initialValues: initialInvoiceData,
    validateOnChange: false,
    validateOnBlur: false,
    validateOnMount: false,
    enableReinitialize: true,
    validate,
    onSubmit: (values) => {
      if (values.sendInvoice && source === InvoiceSources.TB) {
        previewInvoice(values);
      } else {
        if (isBillFromChanged) {
          addNotification({
            message: `Save Billing information to proceed`,
            variant: 'error'
          });
          return;
        }

        sendInvoice(values);
      }
    }
  });

  const saveBillFrom = (): void => {
    setBillFromSelectedCompanyId(tempBillFromSelectedCompanyId);
  };

  const discardBillFrom = (): void => {
    setTempBillFromSelectedCompanyId(billFromSelectedCompanyId);
  };

  const generateAttachmentSizeWarningMessage = (): string => {
    if (!enableZipAttachments) {
      // if zip attachments are disabled, we can't send more than 35 MB
      return `Selected attachments exceed 35 MB. Please remove attachments or decrease file sizes to
            send load docs.`;
    }

    // if zip attachments are enabled, source is QBO and enableSendQboInvoiceInternally is disabled
    // we can't send more than 35 MB because qbo doesn't support zip attachments
    if (!enableSendQboInvoiceInternally && source === InvoiceSources.QBO) {
      return `Selected attachments exceed 35 MB. Please remove attachments or decrease file sizes to
            send load docs.`;
    }

    // if zip attachments are enabled and source is TB or enableSendQboInvoiceInternally is enabled
    // we can send up to 70 MB
    return `Selected attachments exceed ${maxAttachmentSize} MB. You can send all selected
            attachments as .zip archive or remove some attachments and send them separately.`;
  };

  const generateAttachmentSizeWarningButtonLabel = (): string => {
    if (!enableZipAttachments) {
      // if zip attachments are disabled, we can't send more than 35 MB
      return 'Okay';
    }

    // if zip attachments are enabled, source is QBO and enableSendQboInvoiceInternally is disabled
    // we can't send more than 35 MB because qbo doesn't support zip attachments
    if (!enableSendQboInvoiceInternally && source === InvoiceSources.QBO) {
      return 'Okay';
    }

    // if zip attachments are enabled and source is TB or enableSendQboInvoiceInternally is enabled
    // we can send up to 70 MB
    return 'Send as .zip';
  };

  const handleConfirmAttachmentSizeWarningButtonAction = (): void => {
    if (!enableZipAttachments) {
      setAttachmentSizeWarningOpen(false);
      return;
    }

    if (!enableSendQboInvoiceInternally && source === InvoiceSources.QBO) {
      setAttachmentSizeWarningOpen(false);
      return;
    }

    setAttachmentSizeWarningOpen(false);
    setZipAttachments(true);
  };

  // #region constants and variables
  const userMissingCompanyInfo =
    userData &&
    !userData?.tenant?.location; /* TODO: Verify all fields and allow editing them all */
  const quickbooksEnabled = !!accountingSettings?.division_qbo_credentials?.id;
  const toCustomerContact = accountingSettings?.to_cust_contact_email;
  const toCustomerBilling = accountingSettings?.to_cust_billing_email;
  const ccTenantEmail = accountingSettings?.cc_user_email;
  const ccTenantAccounting = accountingSettings?.cc_accounting_email;
  const isBillFromChanged = tempBillFromSelectedCompanyId !== billFromSelectedCompanyId;

  // #region effects
  useEffect(() => {
    open &&
      !!invoiceId &&
      !!accountingSettings &&
      !!invoiceServicesData &&
      (async (): Promise<void> => {
        setIsLoading(true);
        return getData();
      })();
  }, [open, invoiceId, accountingSettings, invoiceServicesData]);

  useEffect(() => {
    (async (): Promise<void> => {
      const { companyIds, carriers } = getCarriersAndCompanyIds(loads, userData) || [];
      loads.map((load) => {
        if (load.division_id) {
          companyIds.push(load.division_id);
        }
      });
      setBillFromCompanyIds(companyIds);
      setCarriers(carriers);
    })();
  }, [JSON.stringify(loads.map((l) => l.id))]);

  useEffect(() => {
    if (zipAttachments) {
      setMaxAttachmentSize(35); // setting max attachment size to 35 MB as compression sometimes doesn't work as expected
    } else {
      setMaxAttachmentSize(35);
    }
  }, [zipAttachments]);

  useEffect(() => {
    if (!enableSendQboInvoiceInternally) {
      setZipAttachments(false);
    }
  }, [enableSendQboInvoiceInternally]);

  useEffect(() => {
    source === InvoiceSources.QBO &&
      setEmailsToSend(Array.from(new Set([...additionalQBOEmails, ...emailsToSendQBO])));
  }, [additionalQBOEmails, emailsToSendQBO, source]);

  useEffect(() => {
    if (!divisionId || !enableFactoring || !open) return;

    getFactoringConfig();
  }, [divisionId, enableFactoring, open]);

  useEffect(() => {
    if (!accountingSettings) {
      setFactorInvoice(false);
      return;
    }

    setFactorInvoice(accountingSettings.send_invoices_to_factoring_by_default);
  }, [accountingSettings]);

  const [isQboLearnMoreModalOpen, setIsQboLearnMoreModalOpen] = useState<boolean>(false);
  const [isInvoiceDisabled, setIsInvoiceDisabled] = useState(false);
  useEffect(() => {
    if (!originalInvoice?.qbo_attributes?.division_qbo_credentials_id) {
      // if invoice was truckbase invoice
      return;
    }

    if (
      originalInvoice?.qbo_attributes?.division_qbo_credentials_id !=
      divisionAccountingSettings?.division_qbo_credentials?.id
    ) {
      setIsInvoiceDisabled(true);
    } else {
      setIsInvoiceDisabled(false);
    }
  }, [
    originalInvoice?.qbo_attributes?.division_qbo_credentials_id,
    divisionAccountingSettings?.division_qbo_credentials?.id
  ]);

  const qboAlertMessage = useMemo<string>((): string => {
    return 'This invoice is associated with a QuickBooks account that has been disconnected. Modifications are no longer possible.';
  }, []);

  const qboAlertActionLabel = useMemo<string>((): string => {
    return 'LEARN MORE';
  }, []);

  const handleQboAlertAction = (): void => {
    setIsQboLearnMoreModalOpen(true);
  };

  const ordersListForUpload = useMemo(() => {
    let ordersList: TwSelectOption[] = [];
    if (loads) {
      ordersList = loads.map((load) => {
        return {
          value: load.id,
          label: load.readable_id as unknown as string
        };
      });
    }
    return ordersList;
  }, [loads]);

  const handleFactorInvoiceChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setFactorInvoice(event.target.checked);
  };

  // #region sales tax

  const isSalesTaxEnabled = !!accountingSettings?.sales_tax_enabled;

  const [salesTaxOptions, setSalesTaxOptions] = useState<SalesTax[]>([]);
  const [isSalesTaxSelectVisible, setIsSalesTaxSelectVisible] = useState(false);
  const [isSalesTaxCreateUpdateModalOpen, setIsSalesTaxCreateUpdateModalOpen] = useState(false);
  const [taxAmount, setTaxAmount] = useState(0);
  const [totalWithTax, setTotalWithTax] = useState(0);
  const [salesTax, setSalesTax] = useState<any | null>(null);

  const getTotal = (loadsRates: { [key: string]: RowData[] }): number => {
    const flatRates = Object.values(loadsRates || {})
      .map((loadRates) => loadRates)
      .flat();
    const totalBalance = flatRates.reduce((acc, r) => {
      if (r.unit === InvoiceServiceUnit.FLAT) {
        return acc + (r.rate || 0);
      }

      let rate = r.rate;
      if (r.unit === InvoiceServiceUnit.PERCENTAGE) {
        rate = (r.rate || 0) / 100;
      }

      return acc + NumberUtils.calculateCurrencyAmount(rate, r.quantity);
    }, 0);

    return totalBalance;
  };

  const onAddSalesTaxClick = (): void => {
    setIsSalesTaxCreateUpdateModalOpen(true);
  };

  const refetchSalesTaxSettings = (): void => {
    SalesTaxService.getSalesTaxSettings(
      divisionId,
      { sort: 'name', orderBy: 'ASC' },
      { is_active: true }
    ).then((settings1) => {
      // fetch separately if we can't find id (if deactivated)
      if (
        originalInvoice?.sales_tax_id &&
        !settings1.find((tax) => tax.id === originalInvoice?.sales_tax_id)
      ) {
        SalesTaxService.getSalesTaxSettings(
          divisionId,
          { sort: 'name', orderBy: 'ASC' },
          { id: [originalInvoice?.sales_tax_id] }
        ).then((settings2) => {
          console.log('settings2', [...settings1, ...settings2]);
          setSalesTaxOptions([...settings1, ...settings2]);
        });
      } else {
        setSalesTaxOptions(settings1);
      }
    });
  };

  useEffect(() => {
    divisionId && refetchSalesTaxSettings();
  }, [divisionId, originalInvoice]);

  useEffect(() => {
    if (loadsRates) {
      const total = getTotal(loadsRates);
      const taxAmount = (total * (salesTax?.value || 0)) / 100;

      setTaxAmount(NumberUtils.roundNumber(taxAmount));
      setTotalWithTax(NumberUtils.roundNumber(total + taxAmount));
    }
  }, [loadsRates, salesTax, values.salesTaxRate]);

  useEffect(() => {
    if (originalInvoice?.sales_tax_id || originalInvoice?.sales_tax_rate) {
      setIsSalesTaxSelectVisible(true);

      const salesTaxOption = salesTaxOptions.find(
        (tax) => tax.id === originalInvoice?.sales_tax_id
      );
      if (salesTaxOption) {
        setSalesTax({
          id: salesTaxOption.id,
          label: salesTaxOption.name,
          value: salesTaxOption.rate
        });
      } else {
        setSalesTax({
          id: originalInvoice?.sales_tax_id,
          label: '(DELETED)',
          value: originalInvoice?.sales_tax_rate
        });
      }
    }
  }, [salesTaxOptions, originalInvoice]);

  // #endregion sales tax

  return (
    <>
      <TwDialog
        open={open}
        onClickClose={(): void => setIsOpenWarning(true)}
        title={clientData ? `Edit invoice for ${clientData.companyName}` : ''}
        dialogTitleContent={quickbooksEnabled && source === InvoiceSources.QBO ? <QBOIcon /> : ''}
        fullScreen={true}
      >
        {isLoading ||
        areAccountingSettingsFetching ||
        areInvoiceServicesFetching ||
        areAccountingSettingsLoading ||
        areInvoiceServicesLoading ? (
          <div className="loading">
            <CircularProgress color="primary" />
          </div>
        ) : (
          <>
            {isInvoiceDisabled && (
              <TwAlert
                message={qboAlertMessage}
                severity="warning"
                icon={<AlertIcon />}
                sx={[
                  {
                    '& .MuiAlert-icon': {
                      padding: '8px 0 0px'
                    },
                    '& .MuiAlert-action': {
                      padding: '0px 0px'
                    }
                  },
                  {
                    backgroundColor: '#F3F4F6',
                    border: '1px solid #E5E7EB',
                    marginBottom: '30px'
                  }
                ]}
                action={
                  <TwButton
                    type={TwButtonType.text}
                    label={qboAlertActionLabel}
                    action={handleQboAlertAction}
                  />
                }
              />
            )}
            <form
              className="edit-tb-invoice"
              onSubmit={handleSubmit}
              style={{ width: originalInvoice?.is_legacy ? '500px' : '800px' }}
            >
              {<span className="edit-invoice__title">Edit invoice details</span>}

              {userMissingCompanyInfo && (
                <span className="edit-invoice__message">
                  Provide additional information to create an invoice in {source || values.source}.
                  The invoice will be added to load card.
                </span>
              )}

              {source === InvoiceSources.TB ? (
                <BillFrom
                  onBillFromEdited={saveBillFromInfo}
                  companyIds={billFromCompanyIds}
                  selectedCompanyId={tempBillFromSelectedCompanyId}
                  onSelectedCompanyChanged={(companyId: string | undefined): void => {
                    setTempBillFromSelectedCompanyId(companyId);
                  }}
                  saveBillFrom={saveBillFrom}
                  discardBillFrom={discardBillFrom}
                  isBillFromChanged={isBillFromChanged}
                  updateCompanyIds={(companyIds: string[], selectedId): void => {
                    setBillFromCompanyIds(companyIds);
                    setTempBillFromSelectedCompanyId(selectedId);
                  }}
                  carriers={carriers}
                  tenantAccountingSettings={accountingSettings}
                />
              ) : (
                <BillFromQBO
                  companyName={accountingSettings?.division_qbo_credentials?.company_name}
                />
              )}

              <BillTo
                clientId={clientData?.companyId ? clientData?.companyId : ''}
                onNewLocation={(location): void => setBillToLocationId(location.id)}
                onBillToEdited={saveBillToInfo}
              />

              <>
                <span className="edit-invoice__information">Invoice information</span>
                <TwField
                  id="invoiceNumber"
                  name="invoiceNumber"
                  value={originalInvoice?.invoice_id || originalInvoice?.invoice_number}
                  label="Invoice number"
                  inputType={TwInputTypes.text}
                  disabled={true}
                  className="edit-invoice__two-column-item"
                />
              </>

              {enableFactoring && showFactorInvoice && (
                <FactorInvoice
                  factorInvoice={factorInvoice}
                  isMultiLoadInvoice={loads.length > 1}
                  factoringCompany={factoringCompanyName || '-'}
                  handleFactorInvoiceChange={handleFactorInvoiceChange}
                />
              )}

              <>
                {originalInvoice?.is_legacy ? (
                  <>
                    <div className="edit-invoice__header">
                      <span>Description</span> <span>Amount</span>
                    </div>
                    <div className="edit-invoice__body">
                      {values?.invoiceItems &&
                        values?.invoiceItems.length > 0 &&
                        values.invoiceItems.map((rate: RateItem, index: number) => (
                          <div key={index}>
                            <InvoiceItems
                              isQbo={source === InvoiceSources.QBO}
                              invoiceItem={rate}
                              onInputChange={handleChange}
                              onAutocompleteChange={(
                                _,
                                newValue: TwAutocompleteOption | null
                              ): void => {
                                setFieldValue(`invoiceItems[${index}].product`, newValue);
                              }}
                              index={index}
                              // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
                              remove={(index): undefined => {
                                const items = [...values.invoiceItems];
                                items.splice(index, 1);
                                setFieldValue('invoiceItems', items);
                                return undefined;
                              }}
                              qboServices={qboServicesList}
                            />
                            {values.invoiceItems.length !== index + 1 && <Divider />}
                          </div>
                        ))}

                      <div className="totals">
                        <Button
                          variant="text"
                          className="totals__add"
                          onClick={(): void => {
                            setFieldValue(`invoiceItems[${values.invoiceItems.length}]`, {
                              description: '',
                              amount: '',
                              product: null
                            });
                          }}
                        >
                          + Add cost
                        </Button>
                        <span className="totals__title">Total</span>
                        <span className="totals__amount">{`${NumberUtils.getCurrency(
                          values.invoiceItems && values.invoiceItems.length > 0
                            ? values?.invoiceItems.reduce(
                                (previousValue: number, currentValue: RateItem) =>
                                  previousValue + Number(currentValue.amount),
                                0
                              )
                            : 0
                        )}`}</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <LoadsRates
                    loads={loads}
                    loadsRates={loadsRates}
                    divisionId={divisionId}
                    source={originalInvoice?.source as InvoiceSources}
                    onUpdateLoadRates={(data: RowData[], loadId: string): void => {
                      setLoadsRates((prev) => {
                        const newData = data.map((row) => {
                          // set default description for new added row
                          if (!('description' in row)) {
                            const getInvoiceDefaultDescription = invoiceDefaultDescriptionMaker(
                              accountingSettings!,
                              commoditiesEnabled
                            );
                            return {
                              ...row,
                              description: getInvoiceDefaultDescription(
                                loads.find((l) => l.id === loadId) || loads[0]
                              )
                            };
                          }
                          return row;
                        });
                        if (prev) {
                          return { ...prev, [loadId]: newData };
                        }
                      });
                    }}
                    disabled={isInvoiceDisabled}
                  />
                )}

                {(isSalesTaxEnabled ||
                  originalInvoice?.sales_tax_id ||
                  originalInvoice?.sales_tax_rate) && (
                  <>
                    <SalesTaxCreateUpdateModal
                      open={isSalesTaxCreateUpdateModalOpen}
                      onClose={(): void => {
                        setIsSalesTaxCreateUpdateModalOpen(false);
                        refetchSalesTaxSettings();
                      }}
                      divisionId={divisionId}
                    />
                    <Grid container position="relative" marginTop="10px">
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            cursor: 'pointer',
                            color: '#3A4DE9',
                            fontWeight: 500,
                            fontSize: '14px',
                            width: '100px',
                            display: isSalesTaxSelectVisible ? 'none' : 'inherit'
                          }}
                          onClick={(): void => {
                            setIsSalesTaxSelectVisible(true);
                          }}
                        >
                          + Add tax
                        </Box>
                        <Box display={isSalesTaxSelectVisible ? 'inherit' : 'none'}>
                          <TwAutocomplete
                            value={salesTax}
                            placeholder="Select tax..."
                            optionList={salesTaxOptions.map((tax) => ({
                              id: tax.id,
                              label: tax.name || '',
                              value: tax.rate || 0
                            }))}
                            onChange={(e, newValue): void => {
                              setSalesTax(newValue);
                            }}
                            name="salesTax"
                            sx={{ width: '200px' }}
                            listAction={{
                              label: 'New Tax',
                              action: onAddSalesTaxClick
                            }}
                            disableClearable={true}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Grid container>
                          {!salesTax ? (
                            <Grid
                              item
                              xs={12}
                              display={isSalesTaxSelectVisible ? 'flex' : 'none'}
                              alignItems="center"
                              justifyContent="flex-end"
                              paddingRight="33px"
                              paddingTop="7px"
                              paddingBottom="15px"
                            >
                              <Typography color="#9CA3AF" fontSize="14px">
                                Select tax to calculate
                              </Typography>
                            </Grid>
                          ) : (
                            <>
                              <Grid
                                item
                                xs={7.7}
                                display={isSalesTaxSelectVisible ? 'flex' : 'none'}
                                justifyContent="flex-end"
                                alignItems="center"
                                paddingTop="8px"
                                paddingBottom="14px"
                              >
                                <Box
                                  component="div"
                                  sx={{
                                    fontFamily: 'Poppins',
                                    fontWeight: 600,
                                    lineHeight: '21px',
                                    letterSpacing: 0,
                                    fontSize: '14px',
                                    color: '#9CA3AF'
                                  }}
                                >
                                  {salesTax?.value || 0} %
                                </Box>
                              </Grid>
                              <Grid
                                item
                                xs={4.3}
                                display={isSalesTaxSelectVisible ? 'flex' : 'none'}
                                alignItems="center"
                                paddingTop="8px"
                                paddingBottom="14px"
                                justifyContent="flex-end"
                                paddingRight="35px"
                              >
                                <Box component="div" fontSize="14px" marginLeft="8px">
                                  <Box component="span" color="#9CA3AF">
                                    ${' '}
                                  </Box>
                                  {taxAmount === 0
                                    ? 0
                                    : NumberUtils.getCurrency(taxAmount).replace('$', '')}
                                </Box>
                              </Grid>
                            </>
                          )}
                          {/* TOTAL */}
                          <Grid
                            item
                            xs={7.7}
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="center"
                          >
                            <Box
                              component="div"
                              sx={{
                                fontFamily: 'Poppins',
                                fontWeight: 600,
                                lineHeight: '21px',
                                letterSpacing: 0,
                                fontSize: '14px',
                                color: '#9CA3AF'
                              }}
                            >
                              {loads.length > 1 ? 'GRAND TOTAL' : 'TOTAL'}
                            </Box>
                          </Grid>
                          <Grid
                            item
                            xs={4.3}
                            display="flex"
                            alignItems="center"
                            justifyContent="flex-end"
                          >
                            <Box
                              component="div"
                              fontSize="14px"
                              marginLeft="8px"
                              paddingRight="35px"
                            >
                              <Box component="span" color="#9CA3AF">
                                ${' '}
                              </Box>
                              {totalWithTax === 0
                                ? 0
                                : NumberUtils.getCurrency(totalWithTax).replace('$', '')}
                            </Box>
                          </Grid>
                        </Grid>
                      </Grid>

                      <IconButton
                        onClick={(): void => {
                          setSalesTax(null);
                          setIsSalesTaxSelectVisible(false);
                          setFieldValue('salesTaxId', '');
                          setFieldValue('salesTaxRate', 0);
                        }}
                        sx={{
                          position: 'absolute',
                          top: '0',
                          right: '0',
                          display: isSalesTaxSelectVisible ? 'flex' : 'none'
                        }}
                      >
                        <CloseIcon
                          sx={{
                            width: '20px',
                            height: '20px'
                          }}
                        />
                      </IconButton>
                    </Grid>
                  </>
                )}
                <span className="edit-invoice__section-title">
                  Select documents you want to attach:
                </span>
                <TwSelectableDocsPhotos
                  photos={!!singleLoad && values && values.photos.length > 0 ? values.photos : []}
                  documents={values && values.documents.length > 0 ? values.documents : []}
                  onSelectDocuments={setSelectedDocs}
                  onSelectPhotos={setSelectedPhotos}
                  showUploadButton={!!singleLoad}
                  onUploadButtonClick={(): void => {
                    setDocumentsOpen(true);
                  }}
                  onDocumentClick={(document): void => {
                    setFileToView(document);
                    setDocsViewerOpen(true);
                  }}
                  onPhotoClick={(photo): void => {
                    setFileToView(photo);
                    setDocsViewerOpen(true);
                  }}
                  disableEncryptedDocuments={mergeAttachments}
                />

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '1rem'
                  }}
                >
                  <TwCheckbox
                    label="Merge attachments"
                    name="mergeAttachments"
                    onInputChange={(): void => {
                      setMergeAttachments((prev: any) => !prev);
                    }}
                    checked={mergeAttachments}
                  />

                  {(source === InvoiceSources.TB ||
                    (source === InvoiceSources.QBO && enableSendQboInvoiceInternally)) &&
                    enableZipAttachments && (
                      <TwCheckbox
                        label="Zip attachments"
                        name="zipAttachments"
                        onInputChange={(): void => {
                          setZipAttachments((prev: any) => !prev);
                        }}
                        checked={zipAttachments}
                      />
                    )}
                </Box>
              </>

              {source === InvoiceSources.TB && (
                <TwField
                  id="specialInstructions"
                  name="specialInstructions"
                  label="Notes (optional)"
                  inputType={TwInputTypes.textarea}
                  value={
                    values.specialInstructions
                      ? values.specialInstructions
                      : originalInvoice?.special_instructions
                  }
                  onInputChange={handleChange}
                  className="edit-invoice__instructions"
                />
              )}

              {source === InvoiceSources.QBO && qboPreferences.classTrackingPerTxn === true && (
                <Box>
                  <TwSelect
                    label="Class"
                    value={values.qboClassId}
                    name="qboClassId"
                    optionList={
                      !initialInvoiceData.qboClassId
                        ? classesDropdown
                        : classesDropdown.filter((e) => e.label !== 'No Class')
                    }
                    onInputChange={handleChange}
                    className="create-invoice__qbo-class__select"
                    labelIcon={
                      <TwTooltip
                        title="Class selection is being pulled from QBO account"
                        placement="top"
                      >
                        <InfoIcon className="create-invoice__qbo-class__icon" />
                      </TwTooltip>
                    }
                  />
                </Box>
              )}

              <div className="edit-invoice__due-date-section">
                <span className="edit-invoice__field-name">Due date</span>
                <div>
                  <TwDatePicker
                    id="custom-due-date"
                    name="customDueDate"
                    value={values.customDueDate ? values.customDueDate : dueDate}
                    placeholder="MM/DD/YY"
                    onChange={(newDate): void => {
                      const date = newDate as Date | null;

                      !!date && setDueDate(date);

                      setFieldValue('customDueDate', date);
                    }}
                  />
                </div>
              </div>

              <Box className="invoice-shary-history">
                <TwShareHistory id={invoiceId} shareType={ShareType.invoice_history} />
              </Box>

              <div className="edit-invoice__footer">
                {quickbooksEnabled &&
                  source === InvoiceSources.QBO &&
                  !enableSendQboInvoiceInternally && (
                    <div className="create-invoice__footer-options">
                      <div style={{ display: 'flex' }}>
                        <TwCheckbox
                          label="Send"
                          name="sendInvoice"
                          onInputChange={handleChange}
                          value={values.sendInvoice}
                          checked={values.sendInvoice}
                        />
                        <TwTooltip
                          title="Mark send invoice directly to customer and additional recipients, and transfer data to Quickbooks"
                          placement="top"
                        >
                          <InfoIcon
                            className="create-invoice__info"
                            sx={{
                              fill: '#D1D5DB',
                              height: '18px',
                              width: '18px'
                            }}
                          />
                        </TwTooltip>
                      </div>

                      {values.sendInvoice && (
                        <>
                          <TwTagsField
                            id="emailListQBO"
                            label="To"
                            tagsDisplayed={emailsToSendQBO}
                            tagType={'email'}
                            suggestedOptions={suggestedEmails}
                            disabled
                          />

                          <TwTagsField
                            id="emailListQBOAdditionalRecipients"
                            label="Add additional recipients"
                            tagsDisplayed={additionalQBOEmails}
                            tagType={'email'}
                            onTagAdded={(emails): void => {
                              handleSelectedAdditionalEmails(emails);
                            }}
                            suggestedOptions={suggestedEmails}
                            placeholder="Optional"
                          />
                        </>
                      )}
                    </div>
                  )}
                {source === InvoiceSources.TB && (
                  <div className="edit-invoice__footer-options">
                    <Box display={'flex'}>
                      <TwCheckbox
                        label="Send invoice"
                        name="sendInvoice"
                        onInputChange={handleChange}
                        value={values.sendInvoice}
                        checked={values.sendInvoice}
                      />
                      <TwTooltip
                        title="Mark checkbox to auto-fill recipient emails based on invoice settings, and edit email fields"
                        placement="top"
                      >
                        <InfoOutlinedIcon className="edit-invoice__info" />
                      </TwTooltip>
                    </Box>
                  </div>
                )}

                <div className={'edit-invoice__footer-tb-actions'}>
                  <TwButton
                    fullwidth={false}
                    label="Cancel"
                    type={TwButtonType.secondary}
                    action={(): void => setIsOpenWarning(true)}
                  />
                  <TwButton
                    fullwidth={false}
                    label={getButtonLabel(values)}
                    type={TwButtonType.primary}
                    action={handleSubmit}
                    disabled={isInvoiceDisabled}
                  />
                </div>
              </div>
            </form>
            {!!singleLoad && (
              <TwUploadOrderTripFiles
                orderId={singleLoad?.id || ''}
                handleClose={(): void => setDocumentsOpen(false)}
                open={documentsOpen}
                entity="order"
                entityList={ordersListForUpload}
                onAddDocumentsSuccess={(): void => {
                  handleDocumentsUpload(setFieldValue);
                }}
                defaultVisibility={isEnabledDriversSeeDocs}
                disableInternalUpload={false}
              />
            )}
          </>
        )}
      </TwDialog>
      <TwDialog open={isOpenWarning} title="Leave page?" onClickClose={closeWarning}>
        <div className="warning">
          <Warning />
          <p className="warning__message">
            You haven’t finished editing the invoice.
            <br /> Do you want to leave without finishing?
          </p>
          <div className="warning__actions">
            <TwButton
              fullwidth={false}
              label="Close without saving"
              type={TwButtonType.secondary}
              action={handleClose}
            />
            <TwButton
              fullwidth={false}
              label="Continue editing"
              type={TwButtonType.primary}
              action={closeWarning}
            />
          </div>
        </div>
      </TwDialog>

      {!!singleLoad && (
        <UploadBol
          loadId={singleLoad.id}
          open={isUploadBolOpen}
          onClose={(): void => {
            dispatch(closeUploadBol());
          }}
          onSuccess={(): void => {
            (async (): Promise<void> => {
              return getData();
            })();
          }}
        />
      )}

      <TwDialog
        open={attachmentSizeWarningOpen}
        title="Unable to send invoice"
        onClickClose={(): void => setAttachmentSizeWarningOpen(false)}
      >
        <div className="warning">
          <Warning />
          <span className="warning__message">{generateAttachmentSizeWarningMessage()}</span>
          <div className="warning__actions">
            <TwButton
              fullwidth={false}
              label={generateAttachmentSizeWarningButtonLabel()}
              type={TwButtonType.primary}
              action={handleConfirmAttachmentSizeWarningButtonAction}
            />
          </div>
        </div>
      </TwDialog>

      <TwDialog
        open={zipAttachmentSizeWarningOpen}
        title="Zip file too large to send invoice"
        onClickClose={(): void => setZipAttachmentSizeWarningOpen(false)}
      >
        <div className="warning">
          <Warning />
          <span className="warning__message">
            Your zipped attachments have exceeded the size limit of {maxAttachmentSize} MB for
            sending an invoice. Please decrease the file sizes or deselect some attachements.
          </span>
          <div className="warning__actions">
            <TwButton
              fullwidth={false}
              label="Got it"
              type={TwButtonType.primary}
              action={(): void => setZipAttachmentSizeWarningOpen(false)}
            />
          </div>
        </div>
      </TwDialog>

      {invoicePreviewOpen && (
        <PreviewInvoice
          tenantAccountingSettings={accountingSettings}
          open={invoicePreviewOpen}
          loading={invoicePreviewLoading}
          invoiceId={invoiceId}
          source={source}
          presetQboEmailTo={emailsToSendQBO}
          presetAdditionalQBOEmails={additionalQBOEmails}
          onSelectedAdditionalEmailsChange={(emails): void =>
            handleSelectedAdditionalEmails(emails)
          }
          onEditClick={(): void => {
            handlePreviewInvoiceClose(false);
          }}
          invoiceDialogClosed={!originalInvoice}
          hasBeenSent={originalInvoice?.sent}
          attachments={invoicePreviewAttachments}
          onClose={(doCloseInvoicePage: boolean): void => {
            handlePreviewInvoiceClose(doCloseInvoicePage);
          }}
          onSaveClick={(): void => {
            handlePreviewInvoiceClose(true);
          }}
          suggestedContacts={suggestedEmails}
          onSelectedEmailTo={(emails): void => handleSelectedEmailsToSend(emails)}
          onSelectedEmailCc={(emails): void => handleSelectedEmailsToCc(emails)}
          presetEmailTo={emailsToSend}
          presetEmailCc={emailsToCc}
          customerName={originalInvoice?.client.name || ''}
          tenantData={{
            userName: `${userData?.first_name} ${userData?.last_name}`,
            companyName: userData?.company_name || ''
          }}
          invoiceNumber={`${originalInvoice?.invoice_id}` || ''}
          customerOrderNumber={
            loads[0]?.customer_order_number
              ? loads.length > 1
                ? `${loads[0].customer_order_number}-${loads.length}`
                : loads[0].customer_order_number
              : ''
          }
          onInvoiceSent={(previewData: PreviewInvoiceSendData): void => {
            sendInvoice(values, previewData);
          }}
          onQBOInvoiceSent={(_, previewData?: PreviewInvoiceSendData): void => {
            sendInvoice(values, previewData, true);
          }}
          onInvoiceDeleted={(): void => {
            handleClose();
            onDeleteInvoiceFromPreview && onDeleteInvoiceFromPreview();
          }}
          canSendInvoice={(): boolean => {
            return canSaveAndOrSendInvoice(values);
          }}
          divisionId={divisionId}
        />
      )}

      {/** Invoice PDF viewer for Truckbase invoice preview page */}
      <TwDocumentViewer
        readableId={readableId}
        blob={previewBlob}
        files={null}
        blobType={TwDocumentType.pdf}
        blobTitle={previewBlobTitle}
        downloadName={previewDownloadName}
        open={invoicePreviewOpen}
        hideCloseIcon={source === InvoiceSources.QBO}
        onClickClose={(): void => handlePreviewInvoiceClose(false)}
        className="edit-invoice__preview-invoice-viewer"
        PaperProps={{
          sx: {
            position: 'absolute',
            width: '68%',
            top: 0,
            right: 0,
            margin: 0,
            height: '100vh',
            maxHeight: '100vh',
            zIndex: '1400'
          }
        }}
      />

      {/** Document and photo preview */}
      {singleLoad && singleLoad.order_attachments && (
        <TwOrderTripsDocViewer
          readableId={singleLoad?.readable_id}
          files={[fileToView]}
          open={docsViewerOpen}
          onClickClose={(): void => setDocsViewerOpen(false)}
          selectedFileId={fileToView.id}
        />
      )}

      <TwQBOLoader open={showQBOLoader} />

      <TwInfoModal
        open={isQboLearnMoreModalOpen}
        title="Why I can't edit this invoice?"
        body={
          <Stack direction="column" spacing="5">
            <Typography
              variant="body2"
              fontWeight="400"
              fontSize="14px"
              color="#6B7280"
              marginBottom="20px"
            >
              This invoice is associated with a QuickBooks account that has been disconnected.
              Modifications to this invoice in Truckbase are no longer possible.
            </Typography>
            <Typography variant="body2" fontWeight="400" fontSize="14px" color="#6B7280">
              To send a new invoice for this load, first delete this invoice. Once deleted, you will
              be able to update load rates and resend the invoice using the current invoicing
              method.
            </Typography>
          </Stack>
        }
        onClickClose={(): void => {
          setIsQboLearnMoreModalOpen(false);
        }}
      />
    </>
  );
};

export default EditInvoice;
